import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useLanguage } from '../../contexts/LanguageContext';
import { WarehouseTransfer } from '../../types/warehouseTransfers.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

interface WarehouseTransferPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  transfer: WarehouseTransfer | null;
  onEdit?: () => void;
}

const WarehouseTransferPreviewDialog: React.FC<WarehouseTransferPreviewDialogProps> = ({
  open,
  onClose,
  transfer,
  onEdit
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      loadCompanyInfo();
    }
  }, [open]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  if (!transfer) return null;

  const handlePrint = () => {
    // بناء محتوى الطباعة

    // بناء محتوى الطباعة
    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #667eea;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#667eea;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const docTitle = isRTL ? 'تحويل بين مخازن' : 'Warehouse Transfer';

    const detailRows = transfer.details && transfer.details.length > 0
      ? transfer.details.map((d, i) => `
        <tr>
          <td style="text-align:center;">${i + 1}</td>
          <td><strong>${d.itemCode}</strong><br/><span style="color:#666;font-size:11px;">${isRTL ? d.itemNameAr : d.itemName}</span></td>
          <td>${isRTL ? d.unitNameAr : d.unitName}</td>
          <td style="text-align:center;font-weight:bold;">${d.requestedQuantity?.toFixed(3) || '0.000'}</td>
          ${showShippedColumn ? `<td style="text-align:center;font-weight:bold;color:#ed6c02;">${d.shippedQuantity?.toFixed(3) || '-'}</td>` : ''}
          ${showReceivedColumn ? `<td style="text-align:center;font-weight:bold;color:#2e7d32;">${d.receivedQuantity?.toFixed(3) || '-'}</td>` : ''}
          <td>${d.notes || '-'}</td>
        </tr>
      `).join('')
      : `<tr><td colspan="${4 + (showShippedColumn ? 1 : 0) + (showReceivedColumn ? 1 : 0) + 1}" style="text-align:center;color:#999;">${isRTL ? 'لا توجد تفاصيل' : 'No details'}</td></tr>`;

    const html = `
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${docTitle} - ${transfer.transferNumber}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; font-size: 13px; color: #333; direction: ${isRTL ? 'rtl' : 'ltr'}; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px 10px; font-size: 12px; }
          th { background: #667eea; color: white; font-weight: bold; }
          tr:nth-child(even) { background: #f9f9f9; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
          .info-item label { font-size: 11px; color: #999; display: block; }
          .info-item span { font-weight: bold; font-size: 14px; }
          .wh-box { padding: 12px; border-radius: 6px; text-align: center; }
          .wh-from { background: #ffebee; border: 1px solid #ef9a9a; }
          .wh-to { background: #e8f5e9; border: 1px solid #a5d6a7; }
          .wh-grid { display: grid; grid-template-columns: 1fr auto 1fr; gap: 10px; align-items: center; margin: 15px 0; }
          .arrow { font-size: 30px; color: #667eea; text-align: center; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-box { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #000; padding-top: 5px; margin-top: 60px; }
          .status { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 11px; font-weight: bold; color: white; }
          .status-Pending { background: #ed6c02; }
          .status-Draft { background: #0288d1; }
          .status-InTransit { background: #9c27b0; }
          .status-Received { background: #1976d2; }
          .status-Posted { background: #2e7d32; }
          .status-Rejected { background: #d32f2f; }
          @page { size: A4; margin: 1cm; }
        </style>
      </head>
      <body>
        ${companyHeader}
        <h1 style="text-align:center;color:#667eea;margin:10px 0 20px;">${docTitle}</h1>

        <div style="border:2px solid #667eea;border-radius:8px;padding:15px;margin-bottom:20px;">
          <div class="info-grid">
            <div class="info-item">
              <label>${isRTL ? 'رقم التحويل' : 'Transfer Number'}</label>
              <span style="color:#667eea;">${transfer.transferNumber}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'التاريخ' : 'Date'}</label>
              <span>${formatDate(transfer.transferDate)}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'الحالة' : 'Status'}</label>
              <span class="status status-${transfer.status}">${getStatusLabel(transfer.status)}</span>
            </div>
            ${transfer.journalEntryId ? `
            <div class="info-item">
              <label>${isRTL ? 'رقم القيد' : 'Journal Entry'}</label>
              <span style="color:#2e7d32;">${transfer.journalEntryNumber || ''} (#${transfer.journalEntryId})</span>
            </div>` : ''}
          </div>

          <hr style="border:none;border-top:1px solid #ddd;margin:10px 0;" />

          <div class="wh-grid">
            <div class="wh-box wh-from">
              <div style="font-size:11px;color:#999;">${isRTL ? 'من المخزن (المصدر)' : 'From Warehouse (Source)'}</div>
              <div style="font-weight:bold;color:#d32f2f;font-size:15px;">${isRTL ? transfer.fromWarehouseNameAr : transfer.fromWarehouseName}</div>
              <div style="font-size:11px;color:#999;">${transfer.fromWarehouseCode}</div>
            </div>
            <div class="arrow">⟶</div>
            <div class="wh-box wh-to">
              <div style="font-size:11px;color:#999;">${isRTL ? 'إلى المخزن (الطالب)' : 'To Warehouse (Requester)'}</div>
              <div style="font-weight:bold;color:#2e7d32;font-size:15px;">${isRTL ? transfer.toWarehouseNameAr : transfer.toWarehouseName}</div>
              <div style="font-size:11px;color:#999;">${transfer.toWarehouseCode}</div>
            </div>
          </div>

          ${transfer.shippedDate || transfer.receivedDate || transfer.postedDate ? `
          <div class="info-grid" style="margin-top:10px;">
            ${transfer.shippedDate ? `<div class="info-item"><label>${isRTL ? 'تاريخ الشحن' : 'Shipped Date'}</label><span>${formatDate(transfer.shippedDate)}</span></div>` : ''}
            ${transfer.receivedDate ? `<div class="info-item"><label>${isRTL ? 'تاريخ الاستلام' : 'Received Date'}</label><span>${formatDate(transfer.receivedDate)}</span></div>` : ''}
            ${transfer.postedDate ? `<div class="info-item"><label>${isRTL ? 'تاريخ الترحيل' : 'Posted Date'}</label><span style="color:#2e7d32;">${formatDate(transfer.postedDate)}</span></div>` : ''}
          </div>` : ''}

          ${transfer.notes ? `<div style="margin-top:10px;"><label style="font-size:11px;color:#999;">${isRTL ? 'ملاحظات:' : 'Notes:'}</label><p>${transfer.notes}</p></div>` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>${isRTL ? 'الصنف' : 'Item'}</th>
              <th>${isRTL ? 'الوحدة' : 'Unit'}</th>
              <th>${isRTL ? 'الكمية المطلوبة' : 'Requested'}</th>
              ${showShippedColumn ? `<th>${isRTL ? 'المشحونة' : 'Shipped'}</th>` : ''}
              ${showReceivedColumn ? `<th>${isRTL ? 'المستلمة' : 'Received'}</th>` : ''}
              <th>${isRTL ? 'ملاحظات' : 'Notes'}</th>
            </tr>
          </thead>
          <tbody>
            ${detailRows}
            <tr style="background:#e8eaf6;font-weight:bold;">
              <td colspan="3" style="text-align:${isRTL ? 'left' : 'right'};">${isRTL ? 'إجمالي الأصناف:' : 'Total Items:'}</td>
              <td style="text-align:center;">${transfer.details?.length || 0}</td>
              ${showShippedColumn ? '<td></td>' : ''}
              ${showReceivedColumn ? '<td></td>' : ''}
              <td></td>
            </tr>
          </tbody>
        </table>

        <div class="signatures">
          <div class="sig-box"><div class="sig-line">${isRTL ? 'أمين المخزن (المصدر)' : 'Warehouse Keeper (Source)'}</div></div>
          <div class="sig-box"><div class="sig-line">${isRTL ? 'السائق' : 'Driver'}</div></div>
          <div class="sig-box"><div class="sig-line">${isRTL ? 'أمين المخزن (الوجهة)' : 'Warehouse Keeper (Destination)'}</div></div>
        </div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 500);
    }
  };

  // ✅ FIXED: أضيفت حالات Pending و Rejected
  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'Pending':
        return isRTL ? 'بانتظار الموافقة' : 'Pending';
      case 'Draft':
        return isRTL ? 'مسودة' : 'Draft';
      case 'InTransit':
        return isRTL ? 'قيد الشحن' : 'In Transit';
      case 'Received':
        return isRTL ? 'مستلم' : 'Received';
      case 'Posted':
        return isRTL ? 'مرحّل' : 'Posted';
      case 'Rejected':
        return isRTL ? 'مرفوض' : 'Rejected';
      case 'Cancelled':
        return isRTL ? 'ملغي' : 'Cancelled';
      default:
        return status || '-';
    }
  };

  // ✅ FIXED: ألوان الحالات الجديدة
  const getStatusColor = (status?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'Pending':
        return 'warning';
      case 'Draft':
        return 'info';
      case 'InTransit':
        return 'secondary';
      case 'Received':
        return 'primary';
      case 'Posted':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  // ✅ FIXED: تحديد الأعمدة حسب الحالة بشكل صحيح
  const showShippedColumn = transfer.status === 'InTransit' || transfer.status === 'Received' || transfer.status === 'Posted';
  const showReceivedColumn = transfer.status === 'Received' || transfer.status === 'Posted';
  // ✅ FIXED: زر التعديل يظهر لـ Pending و Draft
  const canEdit = transfer.status === 'Pending' || transfer.status === 'Draft';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        dir={isRTL ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: isDarkMode ? '#1a1a2e' : 'white'
          }
        }}
      >
        <DialogTitle
          className="no-print"
          sx={{
            background: isDarkMode
              ? 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon />
            <Box component="span">
              {isRTL ? 'معاينة تحويل المخازن' : 'Warehouse Transfer Preview'}
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 3 }}>
          {/* Header Info */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
              borderRadius: 2,
              '@media print': {
                border: '2px solid #667eea',
                borderRadius: 0
              }
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'رقم التحويل' : 'Transfer Number'}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {transfer.transferNumber}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'التاريخ' : 'Date'}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatDate(transfer.transferDate)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'الحالة' : 'Status'}
                </Typography>
                <Box>
                  <Chip
                    label={getStatusLabel(transfer.status)}
                    color={getStatusColor(transfer.status)}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              </Box>
              {transfer.journalEntryId && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {isRTL ? 'رقم القيد' : 'Journal Entry'}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    {transfer.journalEntryNumber || ''} (#{transfer.journalEntryId})
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Warehouses */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 2, alignItems: 'center' }}>
              <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(244,67,54,0.1)' : '#ffebee', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'من المخزن (المصدر)' : 'From Warehouse (Source)'}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="error.main">
                  {isRTL ? transfer.fromWarehouseNameAr : transfer.fromWarehouseName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {transfer.fromWarehouseCode}
                </Typography>
              </Box>

              <SwapHorizIcon sx={{ fontSize: 40, color: 'primary.main' }} />

              <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(76,175,80,0.1)' : '#e8f5e9', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'إلى المخزن (الطالب)' : 'To Warehouse (Requester)'}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  {isRTL ? transfer.toWarehouseNameAr : transfer.toWarehouseName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {transfer.toWarehouseCode}
                </Typography>
              </Box>
            </Box>

            {/* Dates */}
            {(transfer.shippedDate || transfer.receivedDate || transfer.postedDate) && (
              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                {transfer.shippedDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {isRTL ? 'تاريخ الشحن' : 'Shipped Date'}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatDate(transfer.shippedDate)}
                    </Typography>
                  </Box>
                )}
                {transfer.receivedDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {isRTL ? 'تاريخ الاستلام' : 'Received Date'}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatDate(transfer.receivedDate)}
                    </Typography>
                  </Box>
                )}
                {transfer.postedDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {isRTL ? 'تاريخ الترحيل' : 'Posted Date'}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatDate(transfer.postedDate)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {transfer.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                  {isRTL ? 'ملاحظات:' : 'Notes:'}
                </Typography>
                <Typography variant="body2">
                  {transfer.notes}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Items Details */}
          <Paper
            elevation={2}
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: 'hidden',
              '@media print': {
                border: '1px solid #667eea',
                borderRadius: 0
              }
            }}
          >
            <Box
              sx={{
                bgcolor: isDarkMode ? 'rgba(102,126,234,0.3)' : '#667eea',
                color: 'white',
                p: 1.5,
                fontWeight: 'bold',
                '@media print': {
                  bgcolor: '#667eea !important',
                  color: 'white !important',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact',
                }
              }}
            >
              {isRTL ? '📦 تفاصيل الأصناف' : '📦 Items Details'}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {isRTL ? 'الصنف' : 'Item'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {isRTL ? 'الوحدة' : 'Unit'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {isRTL ? 'الكمية المطلوبة' : 'Requested'}
                    </TableCell>
                    {showShippedColumn && (
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        {isRTL ? 'المشحونة' : 'Shipped'}
                      </TableCell>
                    )}
                    {showReceivedColumn && (
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        {isRTL ? 'المستلمة' : 'Received'}
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {isRTL ? 'ملاحظات' : 'Notes'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transfer.details && transfer.details.length > 0 ? (
                    transfer.details.map((detail, idx) => (
                      <TableRow key={detail.transferDetailId || idx} hover>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          <strong>{detail.itemCode}</strong>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {isRTL ? detail.itemNameAr : detail.itemName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {isRTL ? detail.unitNameAr : detail.unitName}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                          {detail.requestedQuantity?.toFixed(3) || '0.000'}
                        </TableCell>
                        {showShippedColumn && (
                          <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'warning.main' }}>
                            {detail.shippedQuantity?.toFixed(3) || '-'}
                          </TableCell>
                        )}
                        {showReceivedColumn && (
                          <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'success.main' }}>
                            {detail.receivedQuantity?.toFixed(3) || '-'}
                          </TableCell>
                        )}
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {detail.notes || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={showShippedColumn && showReceivedColumn ? 7 : showShippedColumn ? 6 : 5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {isRTL ? 'لا توجد تفاصيل' : 'No details'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {transfer.details && transfer.details.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align={isRTL ? 'left' : 'right'} sx={{ fontWeight: 'bold', bgcolor: 'rgba(102,126,234,0.1)', fontSize: '0.85rem' }}>
                        {isRTL ? 'إجمالي الأصناف:' : 'Total Items:'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(102,126,234,0.1)' }}>
                        <Chip label={transfer.details.length} color="primary" size="small" />
                      </TableCell>
                      {showShippedColumn && <TableCell sx={{ bgcolor: 'rgba(102,126,234,0.1)' }} />}
                      {showReceivedColumn && <TableCell sx={{ bgcolor: 'rgba(102,126,234,0.1)' }} />}
                      <TableCell sx={{ bgcolor: 'rgba(102,126,234,0.1)' }} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

        </DialogContent>

        <DialogActions className="no-print" sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
          <Button onClick={onClose} size="large">
            {isRTL ? 'إغلاق' : 'Close'}
          </Button>
          
          {canEdit && onEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onEdit}
              size="large"
              color="primary"
            >
              {isRTL ? 'تعديل' : 'Edit'}
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            size="large"
            sx={{
              background: isDarkMode
                ? 'linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)'
                : 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
            }}
          >
            {isRTL ? 'طباعة' : 'Print'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print handled via new window - no CSS needed */}
    </>
  );
};

export default WarehouseTransferPreviewDialog;